import { RiskRecognitionsRequester } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/RiskRecognitionsRequester';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { Environment } from '@cobliteam/react-environments';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import useUser from '../useUser';
import { RISK_RECOGNITION_HIGHLIGHT_QUERY_KEY, RISK_RECOGNITION_LIST_QUERY_KEY } from './constants';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues.
// `apiHost`, not `herbieApiHost`: this endpoint has no /herbie-1.1 path prefix (ai-ingest service).
const getRiskRecognitionsRequester = () => new RiskRecognitionsRequester({ host: env.apiHost });

// Poll cadence (60s) pra sino/página/card não ficarem stale (sem refetch em window-focus).
const POLL_INTERVAL_MS = 60 * 1000;
// Página keyset pequena o bastante pra o scroll infinito do sino disparar (popover mostra ~4-5 linhas): com
// páginas grandes a 1ª já traria tudo e o `fetchNextPage` nunca seria acionado. A página dedicada carrega
// todas as páginas mesmo (só faz mais roundtrips).
const LIST_PAGE_SIZE = 15;

// Discriminador de sessão nas query keys: esses GETs são escopados por frota (X-Fleet-Id do bouncer); sem
// isso o React Query reusaria cache de outra frota ao trocar de contexto.
const useFleetScope = (): string => {
  // Ler `session` direto (não `getSession()`): getSession usa `invariant` e LANÇA quando a sessão ainda não
  // foi hidratada (ex.: logout, mount precoce), o que quebraria a árvore de render antes do optional chaining.
  const { session } = useUser();
  const fleetId = session?.fleet?.id;
  return fleetId ? String(fleetId) : 'no-fleet';
};

/**
 * Highlight de risco corrente — o item ATIVO (não-arquivado) mais recente da listagem. Alimenta o card
 * flutuante e o toast. Pollado a cada 60s enquanto `enabled`; sem refetch em window-focus / aba oculta.
 * Disabling (`enabled = false`) congela a query mas mantém o último item em cache.
 */
const useRiskRecognitionHighlight = (enabled = false) => {
  const fleetScope = useFleetScope();

  const query = useQuery<RiskRecognitionHighlight[], Error>({
    queryKey: [RISK_RECOGNITION_HIGHLIGHT_QUERY_KEY, fleetScope, 'latest-active'],
    queryFn: () => getRiskRecognitionsRequester().listHighlights({ archived: false, limit: 1 }),
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  // O endpoint devolve array recente→antigo; o item corrente é o primeiro.
  return { ...query, data: query.data?.[0] };
};

/**
 * Listagem paginada de notificações de risco (sino: 1ª página; página dedicada: scroll infinito). Keyset
 * por `before` = `created_at` do item mais antigo da página atual. `archived` filtra ativas/arquivadas
 * (omitir = ambas). Ordem fixa recente→antigo vinda do servidor. Polla a cada 60s pra não ficar stale.
 */
const useRiskRecognitionList = ({ archived }: { archived?: boolean } = {}) => {
  const fleetScope = useFleetScope();

  const query = useInfiniteQuery({
    queryKey: [RISK_RECOGNITION_LIST_QUERY_KEY, fleetScope, archived ?? 'all'],
    queryFn: ({ pageParam }) => getRiskRecognitionsRequester().listHighlights({ before: pageParam ?? undefined, limit: LIST_PAGE_SIZE, archived }),
    initialPageParam: null as string | null,
    // Página cheia → pode haver mais: próxima usa o `created_at` do item mais antigo. Página parcial → fim.
    getNextPageParam: (lastPage) => (lastPage.length === LIST_PAGE_SIZE ? lastPage[lastPage.length - 1]?.created_at : undefined),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  const items = useMemo<RiskRecognitionHighlight[]>(() => query.data?.pages.flat() ?? [], [query.data]);

  return { ...query, items };
};

/**
 * Callback que invalida a listagem de notificações de risco (todas as abas/frotas) — força o sino/página a
 * reconciliar com o servidor. Usado quando o poll do highlight detecta um bucket novo que NÃO vira toast
 * (ex.: evento com mais de 10 min): sem toast, mas o feed precisa refletir o item na hora, sem esperar o
 * próprio poll de 60s. Não toca no highlight (esse já é a fonte que disparou a detecção).
 */
const useInvalidateRiskRecognitionList = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [RISK_RECOGNITION_LIST_QUERY_KEY] });
  }, [queryClient]);
};

export { useInvalidateRiskRecognitionList, useRiskRecognitionHighlight, useRiskRecognitionList };
